import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { ApiError } from './lib/api';
import { startTheme } from './lib/theme';
import './styles.css';
import { DialogHost } from './components/DialogHost';
import { toast } from './lib/motion';
import { installModalA11y } from './lib/modalA11y';

// Light or dark as chosen (the moon button), before the first paint.
startTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      // Coming back to the app or the tab, or back online, shows the latest data.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

// Live data on every device: what someone changes in the browser shows in the phone app (and the other way
// round) without reloading. The screens open now are refreshed every 20 seconds while they're visible, and
// at once when the app comes back to the front (phones don't always report "focus").
// The heavy dashboards (CEO, Intelligence, KPIs, Odoo money views, finance, reports) add up the whole company on the
// server: they refresh every 3 minutes instead (and still at once when the app comes back to the front).
// Exact names (light lists such as kpi-targets, intel-settings or odoo-links keep the 20 seconds).
const HEAVY = /^(ceo|analytics|finance|odoo|kpi|kpi-records|intel-center|intel-accuracy|intel-brands|intel-presales|intel-winloss|scenarios|weekly-sales|reports|dashboards|forecast|forecast-trend)$/;
let lastHeavy = Date.now();
const refreshOpenScreens = (all = false) => {
  // Not the sign-in check while signed out: re-checking it there could replace the sign-in form being filled in.
  if (document.visibilityState === 'visible' && navigator.onLine !== false) {
    const heavyToo = all || Date.now() - lastHeavy >= 180_000;
    if (heavyToo) lastHeavy = Date.now();
    void queryClient.invalidateQueries({
      refetchType: 'active',
      predicate: (q) => (q.queryKey[0] !== 'session' || q.state.data !== undefined) && (heavyToo || !HEAVY.test(String(q.queryKey[0]))),
    });
  }
};
window.setInterval(() => refreshOpenScreens(), 20_000);
document.addEventListener('visibilitychange', () => refreshOpenScreens(true));
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshOpenScreens(true); });

// Files in the CRM (deal documents, submittals, requests): clicking one downloads it right here, in the browser
// and in the installed app alike, with a "Downloading…" note and a clear message when it can't be downloaded.
document.addEventListener('click', (e) => {
  const a = (e.target as Element | null)?.closest?.('a[href^="/api/documents/"]') as HTMLAnchorElement | null;
  if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
  e.preventDefault();
  if (a.dataset.downloading) return;
  a.dataset.downloading = '1';
  const label = a.textContent;
  a.classList.add('dl-busy');
  void (async () => {
    try {
      // Fast way: a short-lived direct link from Microsoft 365; the browser downloads straight from OneDrive.
      const linkRes = await fetch(a.href.replace(/\/open$/, '/link'), { credentials: 'same-origin' });
      if (linkRes.ok) {
        const { url } = (await linkRes.json()) as { url: string };
        const link = document.createElement('a');
        link.href = url; link.rel = 'noopener'; document.body.appendChild(link); link.click(); link.remove();
        return;
      }
      if (linkRes.status === 404) {
        const j = (await linkRes.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(j?.error?.message ?? 'This file is no longer in the deal’s OneDrive folder.');
      }
      // Otherwise: the file passed through the CRM.
      const res = await fetch(a.href, { credentials: 'same-origin' });
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(j?.error?.message ?? `The file couldn’t be downloaded (error ${res.status}).`);
      }
      const cd = res.headers.get('content-disposition') ?? '';
      const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1] ?? '') || /filename="([^"]+)"/i.exec(cd)?.[1] || (label ?? 'file').trim();
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'The file couldn’t be downloaded.');
    } finally {
      a.classList.remove('dl-busy'); delete a.dataset.downloading;
    }
  })();
});

installModalA11y();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <DialogHost />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

// The installable app (phone home screen): see public/sw.js. Only on the real site, not in development.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => { void navigator.serviceWorker.register('/sw.js').catch(() => undefined); });
}

// An app left open (phone home screen, installed on a computer) keeps running the version it started with.
// When the server has a newer one, a bar offers to reload, so nobody stays on an old version.
if (import.meta.env.PROD) {
  const running = document.querySelector('script[type="module"][src*="/assets/"]')?.getAttribute('src') ?? null;
  let offered = false;
  const check = async () => {
    if (offered || !running || document.visibilityState !== 'visible') return;
    try {
      const html = await fetch('/', { cache: 'no-store', credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : ''));
      const latest = /<script[^>]+type="module"[^>]+src="(\/assets\/[^"]+)"/.exec(html)?.[1] ?? /<script[^>]+src="(\/assets\/[^"]+)"/.exec(html)?.[1];
      if (!latest || latest === running) return;
      offered = true;
      void navigator.serviceWorker?.getRegistration().then((r) => r?.update()).catch(() => undefined);
      const bar = document.createElement('div');
      bar.className = 'update-bar';
      bar.setAttribute('role', 'status');
      bar.innerHTML = '<span>A new version of the CRM is ready.</span><button type="button" class="btn small primary">Update now</button>';
      bar.querySelector('button')!.addEventListener('click', () => window.location.reload());
      document.body.appendChild(bar);
    } catch { /* offline: try again later */ }
  };
  document.addEventListener('visibilitychange', () => { void check(); });
  window.setInterval(() => { void check(); }, 5 * 60 * 1000);
  window.setTimeout(() => { void check(); }, 15_000);
}